import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    Button,
    Tab,
    Tabs,
    useTheme,
    useMediaQuery,
    AppBar,
    Toolbar,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Card,
    CardContent,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    Dashboard as DashboardIcon,
    People as PeopleIcon,
    Inventory as InventoryIcon,
    Email as EmailIcon,
    Menu as MenuIcon,
    Logout as LogoutIcon,
    Person as PersonIcon,
    Edit as EditIcon,
    Feedback as FeedbackIcon,
    School as SchoolIcon,
    Psychology as PsychologyIcon,
    Settings as SettingsIcon,
    AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { dashboardService, userService, authService } from '../../services/api';
import { DashboardResponse, Representative, Child } from '../../types';
import ChildrenList from './ChildrenList';
import CountrySelector from '../CountrySelector';
import ReferralSystem from './ReferralSystem';
import { useLanguage } from '../../contexts/LanguageContext';
// Mantener importaciones para uso futuro
// import ProductsList from './ProductsList';
// import InvitationsList from './InvitationsList';
// import { Product, Invitation } from '../../types';
import { AxiosError } from 'axios';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`dashboard-tabpanel-${index}`}
            aria-labelledby={`dashboard-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { language, setLanguage } = useLanguage();
    const t = (es: string, en: string) => language === 'es' ? es : en;
    const [value, setValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
    const [currentUser, setCurrentUser] = useState<Representative | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [editNameDialogOpen, setEditNameDialogOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [editProfileDialogOpen, setEditProfileDialogOpen] = useState(false);
    const [editProfileData, setEditProfileData] = useState({
        full_name: '',
        country: '',
        phone: '',
        birth_date: null as Date | null,
    });
    const [showReferralSystem, setShowReferralSystem] = useState(false);

    useEffect(() => {
        const loadDashboard = async () => {
            try {
                console.log('🔍 Dashboard: Iniciando carga de datos...');
                const [dashboardResponse, userResponse] = await Promise.all([
                    dashboardService.getDashboard(),
                    userService.getCurrentUser()
                ]);
                
                console.log('🔍 Dashboard: dashboardResponse recibido:', dashboardResponse);
                console.log('🔍 Dashboard: userResponse recibido:', userResponse);
                
                setDashboardData(dashboardResponse);
                setCurrentUser(userResponse);
                
                console.log('🔍 Dashboard: currentUser establecido:', userResponse);
                console.log('🔍 Dashboard: dashboardData establecido:', dashboardResponse);
                
                // Guardar datos en localStorage para que ActivityPage pueda acceder
                localStorage.setItem('dashboardData', JSON.stringify(dashboardResponse));
            } catch (error) {
                console.error('Error al cargar el dashboard:', error);
                // Si hay un error de autenticación, redirigir al login
                if (error instanceof AxiosError && error.response?.status === 401) {
                    navigate('/login');
                }
            } finally {
                setLoading(false);
            }
        };

        loadDashboard();
    }, [navigate]);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        if (isMobile) {
            setMobileMenuOpen(false);
        }
    };

    const handleLogout = async () => {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
            await authService.logout(refreshToken);
        }
        navigate('/login');
    };

    const handleMobileMenuToggle = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const handleEditNameClick = () => {
        setNewName(currentUser?.full_name || '');
        setEditNameDialogOpen(true);
    };

    const handleEditNameSave = async () => {
        if (!newName.trim() || !currentUser) return;
        
        try {
            const updatedUser = await userService.updateUser({
                ...currentUser,
                full_name: newName.trim()
            });
            setCurrentUser(updatedUser);
            setEditNameDialogOpen(false);
        } catch (error) {
            console.error('Error al actualizar el nombre:', error);
        }
    };

    const handleEditNameCancel = () => {
        setEditNameDialogOpen(false);
        setNewName('');
    };

    const handleEditProfileClick = () => {
        if (currentUser) {
            setEditProfileData({
                full_name: currentUser.full_name,
                country: currentUser.country || '',
                phone: currentUser.phone || '',
                birth_date: currentUser.birth_date ? new Date(currentUser.birth_date) : null,
            });
            setEditProfileDialogOpen(true);
        }
    };

    const handleEditProfileSave = async () => {
        if (!currentUser) return;
        
        try {
            const response = await userService.updateUser({
                ...currentUser,
                full_name: editProfileData.full_name.trim(),
                country: editProfileData.country.trim(),
                phone: editProfileData.phone.trim(),
                birth_date: editProfileData.birth_date?.toISOString().split('T')[0] || '',
            });
            
            // El backend retorna { success, message, data }
            // Necesitamos extraer los datos del usuario de la respuesta
            const updatedUser = response.data || response;
            
            // Actualizar el estado del usuario actual
            setCurrentUser(prevUser => ({
                ...prevUser,
                ...updatedUser
            }));
            
            setEditProfileDialogOpen(false);
            
            // Mostrar mensaje de éxito
            console.log('✅ Perfil actualizado exitosamente');
            
        } catch (error) {
            console.error('Error al actualizar el perfil:', error);
        }
    };

    const handleEditProfileCancel = () => {
        setEditProfileDialogOpen(false);
    };

    const calculateAge = (birthDate: string): number => {
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        
        return age;
    };

    const tabItems = [
        { 
            label: t('Niños', 'Children'), 
            icon: <PeopleIcon />, 
            component: <ChildrenList 
                children={dashboardData?.children || []} 
            />,
            count: dashboardData?.children.length || 0
        },
        { 
            label: t('Sistema de Referidos', 'Referral System'), 
            icon: <SchoolIcon />, 
            component: <ReferralSystem />,
            count: 0
        },
        // Comentado para uso futuro
        // { 
        //     label: 'Productos', 
        //     icon: <InventoryIcon />, 
        //     component: <ProductsList 
        //         products={dashboardData?.products || []} 
        //     />,
        //     count: dashboardData?.products.length || 0
        // },
        // { 
        //     label: 'Invitaciones', 
        //     icon: <EmailIcon />, 
        //     component: <InvitationsList 
        //         invitations={dashboardData?.invitations || []} 
        //     />,
        //     count: dashboardData?.products.length || 0
        // },
    ];

    if (loading) {
        return (
            <Container>
                <Typography>{t('Cargando...', 'Loading...')}</Typography>
            </Container>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            {/* Botón de traducción en la esquina superior derecha - Solo visible en desktop */}
            {!isMobile && (
                <IconButton
                    onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
                    sx={{
                        position: 'fixed',
                        top: 24,
                        right: 24,
                        zIndex: theme.zIndex.drawer + 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '2px solid white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            transform: 'scale(1.1)',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                        },
                        transition: 'all 0.3s ease'
                    }}
                >
                    <span style={{ fontSize: '24px' }}>
                        {language === 'es' ? '🇺🇸' : '🇪🇸'}
                    </span>
                </IconButton>
            )}

            {/* AppBar para móvil */}
            {isMobile && (
                <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label="menu"
                            onClick={handleMobileMenuToggle}
                            edge="start"
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                            {t('Dashboard', 'Dashboard')}
                        </Typography>
                        {/* Botón de cambio de idioma en el AppBar móvil */}
                        <IconButton 
                            color="inherit" 
                            onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
                            sx={{ 
                                mr: 1,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                }
                            }}
                        >
                            <span style={{ fontSize: '20px' }}>
                                {language === 'es' ? '🇺🇸' : '🇪🇸'}
                            </span>
                        </IconButton>
                        <IconButton color="inherit" onClick={handleLogout}>
                            <LogoutIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
            )}

            {/* Drawer para móvil */}
            {isMobile && (
                <Drawer
                    variant="temporary"
                    open={mobileMenuOpen}
                    onClose={handleMobileMenuToggle}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: 250,
                            pt: 8,
                        },
                    }}
                >
                    <List>
                        {tabItems.map((item, index) => (
                            <ListItem 
                                button 
                                key={index}
                                selected={value === index}
                                onClick={() => handleChange({} as React.SyntheticEvent, index)}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText 
                                    primary={`${item.label} (${item.count})`} 
                                />
                            </ListItem>
                        ))}
                        <ListItem 
                            button 
                            onClick={() => navigate('/compensation')}
                        >
                            <ListItemIcon><AccountBalanceIcon /></ListItemIcon>
                            <ListItemText 
                                primary={t('Sistema de Compensación', 'Compensation System')} 
                            />
                        </ListItem>
                    </List>
                </Drawer>
            )}

                <Container maxWidth="lg" sx={{ mt: { xs: 8, md: 4 }, mb: 4, width: '100%', overflow: 'hidden' }} className="mobile-scroll-container">
                <Grid container spacing={3}>
                    {/* Información del usuario */}
                    <Grid item xs={12}>
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Avatar sx={{ bgcolor: '#1976d2', width: 56, height: 56 }}>
                                        <PersonIcon sx={{ fontSize: 32 }} />
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                                {t('Bienvenido', 'Welcome')}, {currentUser?.full_name || t('Usuario', 'User')}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={handleEditNameClick}
                                                sx={{ 
                                                    p: 0.5,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                                    }
                                                }}
                                            >
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Box>
                                        
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                                            {currentUser?.email}
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                📍 {t('País', 'Country')}: {currentUser?.country || t('No especificado', 'Not specified')}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                📞 {t('Teléfono', 'Phone')}: {currentUser?.phone || t('No especificado', 'Not specified')}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                🎂 {t('Fecha de Nacimiento', 'Birth Date')}: {currentUser?.birth_date ? 
                                                    new Date(currentUser.birth_date).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    }) : t('No especificada', 'Not specified')
                                                }
                                            </Typography>
                                            {currentUser?.birth_date && (
                                                <Typography variant="body2" color="text.secondary">
                                                    👤 {t('Edad', 'Age')}: {calculateAge(currentUser.birth_date)} {t('años', 'years')}
                                                </Typography>
                                            )}
                                        </Box>
                                        
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleEditProfileClick}
                                            startIcon={<EditIcon />}
                                            sx={{ 
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontSize: '0.875rem'
                                            }}
                                        >
                                            {t('Editar Perfil Completo', 'Edit Complete Profile')}
                                        </Button>
                                    </Box>
                                    {!isMobile && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Button 
                                                variant="outlined" 
                                                color="primary"
                                                onClick={() => navigate('/compensation')}
                                                startIcon={<AccountBalanceIcon />}
                                                sx={{ 
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                {t('Sistema de Compensación', 'Compensation System')}
                                            </Button>
                                            <Button 
                                                variant="outlined" 
                                                color="error" 
                                                onClick={handleLogout}
                                                startIcon={<LogoutIcon />}
                                            >
                                                {t('Cerrar Sesión', 'Logout')}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>



                    {/* Tarjetas de Feedback */}
                    <Grid item xs={12}>
                        <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                            {t('📝 Formularios de Feedback', '📝 Feedback Forms')}
                        </Typography>
                        <Grid container spacing={2}>
                            {/* Feedback: Parte 1 */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                            backgroundColor: '#f8f9ff'
                                        }
                                    }}
                                    onClick={() => window.open('https://forms.gle/YfCy7wyPP5a8dpEh8', '_blank')}
                                >
                                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                        <FeedbackIcon sx={{ fontSize: 48, color: '#2196f3', mb: 2 }} />
                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                                            {t('Feedback: Parte 1', 'Feedback: Part 1')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {t('Comparte tu experiencia inicial con Princonser Kids. Ayúdanos a mejorar la primera impresión y el onboarding.', 'Share your initial experience with Princonser Kids. Help us improve the first impression and onboarding.')}
                                        </Typography>
                                        <Button 
                                            variant="contained" 
                                            size="small"
                                            sx={{ 
                                                backgroundColor: '#2196f3',
                                                '&:hover': { backgroundColor: '#1976d2' }
                                            }}
                                        >
                                            {t('Participar', 'Participate')}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Feedback: Parte 2 */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                            backgroundColor: '#fff8f9'
                                        }
                                    }}
                                    onClick={() => window.open('https://forms.gle/gasgNSYspSy7gpp28', '_blank')}
                                >
                                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                        <SchoolIcon sx={{ fontSize: 48, color: '#ff9800', mb: 2 }} />
                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1, color: '#f57c00' }}>
                                            {t('Feedback: Parte 2', 'Feedback: Part 2')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {t('Evalúa el contenido educativo y las actividades. Tu opinión sobre la calidad y efectividad del aprendizaje.', 'Evaluate the educational content and activities. Your opinion on the quality and effectiveness of learning.')}
                                        </Typography>
                                        <Button 
                                            variant="contained" 
                                            size="small"
                                            sx={{ 
                                                backgroundColor: '#ff9800',
                                                '&:hover': { backgroundColor: '#f57c00' }
                                            }}
                                        >
                                            {t('Participar', 'Participate')}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>



                            {/* Feedback: Parte 3 */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                            backgroundColor: '#f9fff8'
                                        }
                                    }}
                                    onClick={() => window.open('https://forms.gle/xxQwM8kyABpbJQj28', '_blank')}
                                >
                                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                        <PsychologyIcon sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1, color: '#388e3c' }}>
                                            {t('Feedback: Parte 3', 'Feedback: Part 3')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {t('Comparte el impacto en el desarrollo de habilidades. Cómo Princonser Kids ha influido en el crecimiento de los niños.', 'Share the impact on skill development. How Princonser Kids has influenced children\'s growth.')}
                                        </Typography>
                                        <Button 
                                            variant="contained" 
                                            size="small"
                                            sx={{ 
                                                backgroundColor: '#4caf50',
                                                '&:hover': { backgroundColor: '#388e3c' }
                                            }}
                                        >
                                            {t('Participar', 'Participate')}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Feedback: Sistema Princonser Kids */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                            backgroundColor: '#f8f8ff'
                                        }
                                    }}
                                    onClick={() => window.open('https://forms.gle/FJirsdoc3FDV7hQP8', '_blank')}
                                >
                                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                        <SettingsIcon sx={{ fontSize: 48, color: '#9c27b0', mb: 2 }} />
                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1, color: '#7b1fa2' }}>
                                            {t('Feedback: Sistema', 'Feedback: System')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {t('Evalúa la plataforma y experiencia de usuario. Ayúdanos a mejorar la tecnología y funcionalidad del sistema.', 'Evaluate the platform and user experience. Help us improve the technology and functionality of the system.')}
                                        </Typography>
                                        <Button 
                                            variant="contained" 
                                            size="small"
                                            sx={{ 
                                                backgroundColor: '#9c27b0',
                                                '&:hover': { backgroundColor: '#7b1fa2' }
                                            }}
                                        >
                                            {t('Participar', 'Participate')}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Contenido principal */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column' }}>
                            {/* Tabs para desktop */}
                            {!isMobile && (
                                <>
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                        <Tabs 
                                            value={value} 
                                            onChange={handleChange} 
                                            aria-label="dashboard tabs"
                                            variant="fullWidth"
                                            sx={{
                                                '& .MuiTab-root': {
                                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                                    minHeight: { xs: 48, sm: 64 }
                                                }
                                            }}
                                        >
                                            {tabItems.map((item, index) => (
                                                <Tab 
                                                    key={index}
                                                    label={`${item.label} (${item.count})`}
                                                    icon={item.icon}
                                                    iconPosition="start"
                                                />
                                            ))}
                                        </Tabs>
                                    </Box>
                                    {tabItems.map((item, index) => (
                                        <TabPanel key={index} value={value} index={index}>
                                            {item.component}
                                        </TabPanel>
                                    ))}
                                </>
                            )}

                            {/* Contenido para móvil */}
                            {isMobile && (
                                <Box sx={{ mt: 2 }}>
                                    {tabItems[value].component}
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            {/* Diálogo para editar nombre */}
            <Dialog open={editNameDialogOpen} onClose={handleEditNameCancel} maxWidth="sm" fullWidth>
                <DialogTitle>{t('Editar Nombre', 'Edit Name')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('Nombre completo', 'Full name')}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditNameCancel}>{t('Cancelar', 'Cancel')}</Button>
                    <Button onClick={handleEditNameSave} variant="contained">
                        {t('Guardar', 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo para editar perfil */}
            <Dialog open={editProfileDialogOpen} onClose={handleEditProfileCancel} maxWidth="sm" fullWidth>
                <DialogTitle>{t('Editar Perfil', 'Edit Profile')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('Nombre completo', 'Full name')}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={editProfileData.full_name}
                        onChange={(e) => setEditProfileData({ ...editProfileData, full_name: e.target.value })}
                        sx={{ mt: 1 }}
                    />
                    <CountrySelector
                        value={editProfileData.country}
                        onChange={(value) => setEditProfileData({ ...editProfileData, country: value })}
                        label={t('País', 'Country')}
                        placeholder={t('Selecciona tu país', 'Select your country')}
                        required
                        sx={{ mt: 1 }}
                    />
                    <TextField
                        margin="dense"
                        label={t('Teléfono', 'Phone')}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={editProfileData.phone}
                        onChange={(e) => setEditProfileData({ ...editProfileData, phone: e.target.value })}
                        sx={{ mt: 1 }}
                    />
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={language === 'es' ? es : undefined}>
                        <DatePicker
                            label={t('Fecha de Nacimiento', 'Birth Date')}
                            value={editProfileData.birth_date}
                            onChange={(date) => setEditProfileData({ ...editProfileData, birth_date: date })}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    margin: 'dense',
                                    variant: 'outlined',
                                    sx: { mt: 1 }
                                },
                            }}
                        />
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditProfileCancel}>{t('Cancelar', 'Cancel')}</Button>
                    <Button onClick={handleEditProfileSave} variant="contained">
                        {t('Guardar', 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Dashboard; 